import React from 'react';
import {
  DollarSign,
  TrendingUp,
  Clock,
  Activity,
  Globe,
  BarChart3,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  CartesianGrid,
} from 'recharts';
import { getStatusStyle, CHART_SERIES } from '../../config/statusColors';
import {
  calcPortfolioStats,
  groupByCountry,
  groupByInstrument,
  formatCurrency,
  calcMarketValue,
} from '../../lib/fixedIncome';

const OverviewTab = ({ positions, performanceData }) => {
  const stats = calcPortfolioStats(positions);

  const countryData = groupByCountry(positions);
  const instrumentData = groupByInstrument(positions);

  const pieData = countryData.map((d) => ({
    name: d.country,
    value: Math.round(d.marketValue),
  }));

  const barData = instrumentData.map((d) => ({
    name: d.instrument,
    value: Math.round(d.marketValue),
  }));

  const topPositions = [...positions]
    .sort((a, b) => calcMarketValue(b) - calcMarketValue(a))
    .slice(0, 5);

  const sparkData = (performanceData || []).slice(-6).map((d) => ({
    month: d.month,
    cumTwr: d.cumTwr * 100,
  }));

  const kpiCards = [
    {
      title: 'Total AUM',
      value: formatCurrency(stats.totalMarketValue),
      icon: DollarSign,
      subtitle: null,
    },
    {
      title: 'Weighted Avg Yield',
      value: (stats.weightedAvgYield * 100).toFixed(2) + '%',
      icon: TrendingUp,
      subtitle: null,
    },
    {
      title: 'Weighted Avg Duration',
      value: stats.weightedAvgDuration.toFixed(2) + ' yrs',
      icon: Activity,
      subtitle: null,
    },
    {
      title: 'Active Positions',
      value: stats.activeCount,
      icon: BarChart3,
      subtitle: `${positions.length} total`,
    },
    {
      title: 'Maturing < 91 Days',
      value: stats.maturingCount,
      icon: Clock,
      subtitle: null,
    },
    {
      title: 'Unrealized Foreign Exchange P&L',
      value: formatCurrency(stats.unrealizedFxPnl),
      icon: Globe,
      subtitle: null,
      isFx: true,
    },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpiCards.map((card) => {
          const Icon = card.icon;
          const fxColor = card.isFx
            ? stats.unrealizedFxPnl >= 0
              ? 'text-green-600'
              : 'text-red-600'
            : 'text-black';

          return (
            <div
              key={card.title}
              className="bg-white rounded-none border border-zinc-200 p-5 hover:border-[#4D4D4D] transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400">
                    {card.title}
                  </p>
                  <p className={`text-2xl font-bold font-sans mt-1 ${fxColor}`}>
                    {card.value}
                  </p>
                  {card.subtitle && (
                    <p className="font-mono text-[10px] text-zinc-500 mt-1">{card.subtitle}</p>
                  )}
                </div>
                <div className="p-3 border border-zinc-300 bg-zinc-50 rounded-none">
                  <Icon className="w-5 h-5 text-black" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Geographic Allocation & Instrument Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Geographic Allocation Donut */}
        <div className="bg-white rounded-none shadow-sm border border-gray-100 p-6">
          <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-black font-bold mb-4">
            Geographic Allocation
          </h3>
          <div className="flex items-center gap-6">
            <div className="w-48 h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={CHART_SERIES[i % CHART_SERIES.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2">
              {pieData.map((item, i) => (
                <div key={item.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3"
                      style={{ backgroundColor: CHART_SERIES[i % CHART_SERIES.length] }}
                    />
                    <span className="text-gray-600">{item.name}</span>
                  </div>
                  <span className="font-medium text-gray-900">{formatCurrency(item.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Instrument Type Horizontal Bar */}
        <div className="bg-white rounded-none shadow-sm border border-gray-100 p-6">
          <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-black font-bold mb-4">
            Instrument Type
          </h3>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={barData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                <YAxis type="category" dataKey="name" width={120} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="value" fill={CHART_SERIES[0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Performance Sparkline & Top 5 Positions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 6-Month Performance */}
        <div className="bg-white rounded-none shadow-sm border border-gray-100 p-6">
          <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-black font-bold mb-4">
            6-Month Performance
          </h3>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparkData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(v) => `${v.toFixed(1)}%`} />
                <Tooltip formatter={(v) => `${v.toFixed(2)}%`} />
                <Area
                  type="monotone"
                  dataKey="cumTwr"
                  fill={CHART_SERIES[0]}
                  stroke={CHART_SERIES[1]}
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top 5 Positions Table */}
        <div className="bg-white rounded-none shadow-sm border border-gray-100 p-6">
          <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-black font-bold mb-4">
            Top 5 Positions
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Country
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Instrument
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Face Value
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Market Value
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    YTM
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {topPositions.map((pos, i) => {
                  const statusStyle = getStatusStyle(pos.status);
                  return (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm text-gray-900">{pos.country}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{pos.instrument}</td>
                      <td className="px-3 py-2 text-sm text-gray-900 text-right">
                        {formatCurrency(pos.faceValue)}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900 text-right">
                        {formatCurrency(calcMarketValue(pos))}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900 text-right">
                        {(pos.ytm * 100).toFixed(2)}%
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full"
                          style={statusStyle}
                        >
                          {pos.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
